// AWS accounts (#420): the accounts a workspace has connected, the connections in
// flight, and the flow that connects one.
//
// The flow says what it is asking for before it asks: the role the stack makes
// is read-only, it trusts this platform's account with an external id the
// customer never has to handle, and reading a resource's contents is a separate
// consent answered per request. Then the launch — a link into the customer's own
// CloudFormation console when the estate has published its template, or the
// template and its parameters to launch by hand when it has not
// (`launch_available`) — and then the checks until the role is verified (#824).
// Nothing here reaches AWS from the browser: the customer launches the stack, the
// stack calls the platform, the platform assumes the role, and the screen re-reads
// until it sees the answer.

import { readingAsOrg, type OrgKind } from "../api/memberships";
import { Fragment, useEffect, useRef, useState, type FormEvent, type ReactNode, type Ref } from "react";

import {
  ACCOUNT_NAME_MAX_CHARS,
  ACCOUNT_NAME_RULE,
  PENDING_LIFETIME_DAYS,
  REGIONS,
  STATE_TEXT,
  TEMPLATE_PATH,
  type AccountsRead,
  type ConnectedAccount,
  type Connection,
  type Started,
} from "../api/accounts";
import type { AvailabilityView, ScanAvailability } from "../api/posture";
import type { Setup } from "../api/sources";
import { messageOf } from "../api/http";
import { DEFAULT_DRY_RUN_DAYS } from "../api/sources";
import { roleHolds, type Role } from "../api/roles";
import { onOwnMembership, type SessionResponse } from "../api/session";
import { AccountName } from "./AccountName";
import { Chip, Empty, Field, Notice, PageIntro } from "./chrome";
import { INBOX_KINDS } from "./inboxView";
import { SetupTrail, inFirstRun } from "./SetupTrail";
import { lastScannedWords, ScanControl } from "./ScanControl";
import { copyText } from "./SlackFlow";
import { StepFlow, type FlowStep } from "./StepFlow";

/** What starting, checking and relaunching a connection take on the server (`connections.WRITE_PERMISSION`). */
const CONNECT_PERMISSION = "accounts:manage";
/** What starting a dry run on an alert source takes on the server (`sources.WRITE_PERMISSION`). */
const DRY_RUN_PERMISSION = "alerts:manage";

export type ConnectStep = "configure" | "deploy" | "verify" | "done";

export const CONNECT_STEPS: ReadonlyArray<FlowStep<ConnectStep>> = [
  { id: "configure", label: "Configure" },
  { id: "deploy", label: "Deploy" },
  { id: "verify", label: "Verify" },
  { id: "done", label: "Done" },
];

/** Where the person has put the flow: nothing started yet, or a started connection's launch or checks. */
export type ConnectFlowAt = { at: "configure" } | { at: "deploy" | "verify"; started: Started };

/**
 * The step a flow shows, from where the person put it and what the connection has
 * become since: a verified role is done wherever the person is, and a stack that
 * reports in while the launch is on screen moves the flow on to its checks, whatever
 * they found. The server verifies inside the stack's own report, so a read almost never
 * sees `verifying`: what it sees is a newer report, ending `connected` or `failed`. A
 * launch shown again for a connection that had already come to something stays shown:
 * the person asked for it, and nothing has happened since.
 */
export function connectStepOf(flow: ConnectFlowAt, live: Connection | null): ConnectStep {
  if (flow.at === "configure") return "configure";
  if (live?.state === "connected") return "done";
  if (flow.at === "deploy" && live !== null) {
    const reported = live.callback_received_at !== flow.started.callback_received_at;
    const moved = live.state !== flow.started.state && live.state !== "awaiting_stack";
    if (reported || moved) return "verify";
  }
  return flow.at;
}

/** How long a stack may go without reporting in before the page says so (#825). */
export const SLOW_AFTER_MS = 10 * 60 * 1000;

/**
 * When the page says a stack is slow: ten minutes after the connection started, by the
 * server's start time so a reload does not restart the count, or ten minutes after the
 * person last chose to keep waiting. Null once the stack has reported in.
 */
export function slowDueAt(connection: Connection, waitedAt: number | null): number | null {
  const reported =
    connection.callback_received_at !== null || connection.state === "connected" || connection.state === "verifying";
  if (reported) return null;
  const started = Date.parse(connection.created_at);
  return Math.max(started, waitedAt ?? started) + SLOW_AFTER_MS;
}

/** How long, in the unit a person would say it in: minutes, then hours, then days. */
export function elapsedWords(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000));
  const [count, unit] =
    minutes < 120 ? [minutes, "minute"] : minutes < 48 * 60 ? [Math.floor(minutes / 60), "hour"] : [Math.floor(minutes / 1440), "day"];
  return `${count} ${unit}${count === 1 ? "" : "s"}`;
}

/** The time now, read again when `due` comes round, so what is due shows without a re-read. */
function useNowAt(due: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (due === null) return;
    const wait = due - Date.now();
    if (wait <= 0) {
      setNow(Date.now());
      return;
    }
    const timer = setTimeout(() => setNow(Date.now()), wait);
    return () => clearTimeout(timer);
  }, [due]);
  return now;
}

/** "a", "a or b", "a, b or c". */
const either = (words: string[]): string =>
  words.length < 2 ? words.join("") : `${words.slice(0, -1).join(", ")} or ${words[words.length - 1]}`;

/**
 * Whether this session may connect an account, as the server would answer it: on its
 * own membership, with a role holding `accounts:manage`. Otherwise what to say instead,
 * which is nothing while the roles are still being read.
 */
function connectAccess(
  session: SessionResponse,
  roles: Role[] | null,
  rolesError: string | null,
): { may: true } | { may: false; say: ReactNode } {
  if (!onOwnMembership(session)) {
    return {
      may: false,
      say: <p className="panel__body muted">Only this workspace's own members can connect its AWS accounts.</p>,
    };
  }
  if (rolesError !== null) {
    return {
      may: false,
      say: (
        <div className="panel__body">
          <Notice tone="bad">
            Could not read what your role allows, so connecting is not offered: {rolesError} Refresh to try again.
          </Notice>
        </div>
      ),
    };
  }
  if (roles === null) return { may: false, say: null };
  if (roleHolds(roles, session.role, CONNECT_PERMISSION)) return { may: true };
  const holders = roles.filter((role) => roleHolds(roles, role.role_id, CONNECT_PERMISSION)).map((role) => role.name);
  return {
    may: false,
    say: (
      <p className="panel__body muted">
        {holders.length === 0
          ? "Your role cannot connect an AWS account. Ask the workspace's owner."
          : `Connecting an AWS account needs the ${either(holders)} role. Ask someone in this workspace who has it.`}
      </p>
    ),
  };
}

export function AccountsPage({
  session,
  orgKind = null,
  accounts,
  error,
  roles = null,
  rolesError = null,
  onStart,
  onRename,
  onDisconnect,
  onRelaunch,
  onCheck,
  onCancel,
  onReload,
  onWatch,
  onSetDryRun,
  setup = null,
  scans,
}: {
  session: SessionResponse;
  /**
   * Whether each account can be scanned, as the server answers it, and Run scan (#1274).
   * Absent where the console has no use for it; `availability` null when it could not be read.
   */
  scans?: { availability: AvailabilityView | null; onRun: (cloudAccountId: string) => Promise<void> };
  accounts: AccountsRead | null;
  /** What is left before the first investigation, or null while unread (#805). */
  setup?: Setup | null;
  error: string | null;
  /** The workspace's roles, which say whether this session may connect (#824). */
  roles?: Role[] | null;
  rolesError?: string | null;
  onStart: (regions: string[], name: string | null) => Promise<Started>;
  /** Name an account, or clear its name with null (#823). */
  onRename: (cloudAccountId: string, name: string | null) => Promise<void>;
  /** Disconnect a connected account (#828); the accounts are read again before it resolves. */
  onDisconnect?: (cloudAccountId: string) => Promise<void>;
  onRelaunch: (connectionId: string) => Promise<Started>;
  onCheck: (connectionId: string, accountId: string | null) => Promise<Connection>;
  /** Cancel a connection that is not connected yet (#825). */
  onCancel?: (connectionId: string) => Promise<void>;
  onReload: () => void;
  /** Keep re-reading while the flow waits on a started connection, whatever its state (#824). */
  onWatch?: (on: boolean) => void;
  /** Start a dry run on the alert source the connection made (#860). */
  onSetDryRun?: (sourceId: string, days: number) => Promise<unknown>;
  /** The kind of the org this context reads under, for the org-context empty state (#1205). */
  orgKind?: OrgKind | null;
}) {
  const [flow, setFlow] = useState<ConnectFlowAt | null>(null);
  const [openedByHand, setOpenedByHand] = useState(false);
  const access = connectAccess(session, roles, rolesError);
  const none = accounts !== null && accounts.accounts.length === 0;
  // With no account yet the flow is the page's point, so it is open; once there is one,
  // the list comes first and the flow waits behind a button.
  const shown: ConnectFlowAt | null =
    access.may && session.workspace_id !== null ? (flow ?? (none ? { at: "configure" } : null)) : null;
  const live: Started | null =
    shown === null || shown.at === "configure"
      ? null
      : { ...shown.started, ...accounts?.connections.find((connection) => connection.id === shown.started.id) };
  // A started connection at Deploy or Verify is waited on: a relaunch leaves a failed row
  // failed, and only a re-read sees the stack's next report. Done, closed or gone, it is not.
  const watching = shown !== null && live !== null && connectStepOf(shown, live) !== "done";
  const watch = useRef(onWatch);
  watch.current = onWatch;
  useEffect(() => {
    watch.current?.(watching);
  }, [watching]);
  useEffect(() => () => watch.current?.(false), []);
  if (session.workspace_id === null) {
    return (
      <>
        <PageIntro destination="accounts" />
        <section className="panel">
          <Empty title="Accounts belong to a workspace">
            <p>{readingAsOrg(orgKind, "connect its accounts")}</p>
          </Empty>
        </section>
      </>
    );
  }
  // The flow's own connection is checked in the flow, not listed twice.
  const pending = accounts?.connections.filter((c) => c.state !== "connected" && c.id !== live?.id) ?? [];
  const put = (next: ConnectFlowAt | null) => {
    setOpenedByHand(true);
    setFlow(next);
  };
  const relaunch = async (connectionId: string) => put({ at: "deploy", started: await onRelaunch(connectionId) });
  return (
    <>
      <PageIntro
        destination="accounts"
        action={
          <button type="button" className="btn" onClick={onReload}>
            Refresh
          </button>
        }
      />
      <SetupTrail current="accounts" setup={setup} />
      {error === null ? null : <Notice tone="bad">{error} Refresh to try again.</Notice>}
      {inFirstRun(setup) && accounts?.accounts.some((account) => account.status === "active") ? (
        <section className="setup-next" aria-label="Account connected">
          <div><h2>Your account is connected</h2><p className="muted">Next, give your monitoring a destination for its alerts.</p></div>
          <a className="btn btn--primary" href="/sources">Continue to alert sources</a>
        </section>
      ) : null}
      {pending.length > 0 ? (
        <section className="panel" data-testid="pending">
          <div className="panel__head">
            <h2>Connections in progress</h2>
            <Chip tone="warn">{pending.length}</Chip>
          </div>
          {pending.map((connection) => (
            <Pending
              key={connection.id}
              connection={connection}
              launchAvailable={accounts?.launch_available ?? true}
              onCheck={access.may ? onCheck : undefined}
              onRelaunch={relaunch}
              onCancel={access.may ? onCancel : undefined}
            />
          ))}
        </section>
      ) : null}
      <section className="panel setup-account" data-testid="connected">
        <div className="panel__head">
          <h2>Connected accounts</h2>
          {accounts === null ? null : (
            <Chip>{accounts.accounts.filter((account) => account.status === "active").length} connected</Chip>
          )}
        </div>
        {accounts === null && error === null ? (
          <p className="muted panel__body" role="status">
            Reading this workspace's accounts…
          </p>
        ) : null}
        {/* Where the flow is open on an empty workspace, it is the way on; no empty state above it. */}
        {none && shown === null ? <Empty title="No account connected yet" mark="○" /> : null}
        {accounts !== null && accounts.accounts.length > 0 ? (
          <ul className="setup-account-cards" aria-label="Connected AWS accounts">
            {accounts.accounts.map((account) => (
              <AccountCard
                key={account.id}
                account={account}
                stacks={accounts.connections.filter((connection) => connection.cloud_account_id === account.id)}
                onRename={access.may ? onRename : undefined}
                onDisconnect={access.may ? onDisconnect : undefined}
                scan={
                  scans === undefined
                    ? undefined
                    : {
                        account: scans.availability?.accounts.find((a) => a.cloud_account_id === account.id),
                        scanRoles: scans.availability?.scan_roles ?? [],
                        onRun: scans.onRun,
                      }
                }
              />
            ))}
          </ul>
        ) : null}
        {!access.may ? (
          access.say
        ) : shown === null ? (
          accounts === null ? null : (
            <div className="panel__body actions">
              <button type="button" className="btn btn--primary" onClick={() => put({ at: "configure" })}>
                Connect account
              </button>
            </div>
          )
        ) : (
          <ConnectFlow
            key={shown.at === "configure" ? "new" : shown.started.id}
            flow={shown}
            live={live}
            launchAvailable={accounts?.launch_available ?? true}
            focusOnOpen={openedByHand}
            onStart={async (regions, name) => put({ at: "deploy", started: await onStart(regions, name) })}
            onLaunched={() => {
              if (shown.at !== "configure") put({ at: "verify", started: shown.started });
            }}
            onRelaunch={relaunch}
            onCheck={onCheck}
            onCancel={
              onCancel === undefined
                ? undefined
                : async (connectionId) => {
                    await onCancel(connectionId);
                    put(null);
                  }
            }
            onAnother={() => put({ at: "configure" })}
            onClose={none ? undefined : () => put(null)}
            onSetDryRun={roleHolds(roles, session.role, DRY_RUN_PERMISSION) ? onSetDryRun : undefined}
          />
        )}
      </section>
    </>
  );
}

/** What a person reads for an account's status; any other status shows as the server names it. */
const ACCOUNT_STATUS_TEXT: Record<string, { label: string; tone: "good" | "bad" | "warn" }> = {
  active: { label: "Connected", tone: "good" },
  disconnected: { label: "Disconnected", tone: "warn" },
  revoked: { label: "Revoked", tone: "bad" },
};

/**
 * An account the workspace connected: its name, with the 12-digit id on hover, or the id
 * where it has no name (#823); for someone the server lets name it, the name edited in
 * place; and, while it is connected, Disconnect (#828), confirmed before it is sent, by a
 * question that names each role deleting the stack removes (#1100). Once
 * disconnected the card says what has stopped and when (no credentials are issued for an
 * account that is not connected: #1093), that the role stays until the stack is deleted,
 * and which stack that is.
 */
function AccountCard({
  account,
  stacks,
  onRename,
  onDisconnect,
  scan,
}: {
  account: ConnectedAccount;
  /** The connections that reached this account: their stacks are what the customer deletes. */
  stacks: Connection[];
  /** Absent for someone without `accounts:manage` on their own membership: the server refuses them. */
  onRename?: (cloudAccountId: string, name: string | null) => Promise<void>;
  /** Absent for the same people, for the same reason. */
  onDisconnect?: (cloudAccountId: string) => Promise<void>;
  /** The account as the scan admission policy answers it (#1274); its `account` is absent unread. */
  scan?: {
    account: ScanAvailability | undefined;
    scanRoles: string[];
    onRun: (cloudAccountId: string) => Promise<void>;
  };
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(account.name ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  // Each step replaces the control that held the focus, so the focus is moved by hand: to
  // the question, back to Disconnect, to the refusal, or to what the disconnect left to do.
  const [focusOn, setFocusOn] = useState<"question" | "opener" | "refusal" | "stopped" | null>(null);
  const question = useRef<HTMLParagraphElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const refused = useRef<HTMLParagraphElement>(null);
  const stopped = useRef<HTMLParagraphElement>(null);
  const connected = account.status === "active";
  useEffect(() => {
    if (focusOn === null) return;
    // "stopped" waits for the read that shows the account disconnected.
    const target = { question, opener, refusal: refused, stopped }[focusOn].current;
    if (target === null) return;
    target.focus();
    setFocusOn(null);
  }, [focusOn, connected]);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await onRename?.(account.id, name.trim() === "" ? null : name.trim());
      setEditing(false);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };
  const disconnect = async () => {
    setRefusal(null);
    setDisconnecting(true);
    try {
      await onDisconnect?.(account.id);
      setConfirming(false);
      setFocusOn("stopped");
    } catch (failure) {
      setRefusal(messageOf(failure));
      setFocusOn("refusal");
    } finally {
      setDisconnecting(false);
    }
  };
  const status = ACCOUNT_STATUS_TEXT[account.status] ?? { label: account.status, tone: "bad" as const };
  const mayDisconnect = connected && onDisconnect !== undefined;
  return (
    <li className="setup-account-card" data-testid="account-card" data-account={account.id}>
      <div className="row row--between row--wrap">
        <h3>
          <AccountName id={account.account_id} name={account.name} />
        </h3>
        <Chip tone={status.tone}>{status.label}</Chip>
      </div>
      <p className="muted">Regions: {account.regions.join(", ")}</p>
      {scan?.account === undefined ? null : (
        <div className="stack stack--tight" data-testid="account-scan">
          <p className="muted">{lastScannedWords(scan.account)}</p>
          <ScanControl account={scan.account} scanRoles={scan.scanRoles} onRun={scan.onRun} />
        </div>
      )}
      {account.status === "disconnected" ? (
        <Disconnected stacks={stacks} operatorRole={Boolean(account.operator_role_arn)} focusRef={stopped} />
      ) : null}
      {account.status === "revoked" ? (
        <p className="muted">
          Its stack was deleted in AWS, so Torque Assist can no longer read this account. To connect it again, use
          Connect account and launch the stack it gives you.
        </p>
      ) : null}
      {mayDisconnect && confirming ? (
        <div className="stack stack--tight" data-testid="disconnect-confirm">
          <Notice focusRef={question}>
            <span>
              Disconnect <AccountName id={account.account_id} name={account.name} />? Torque Assist will start no new
              investigations or posture scans on this account, and will stop accepting alarms from its topics. Its
              investigations will get no more credentials for the account: one that is already running can read it
              for up to {CREDENTIAL_MINUTES} minutes more, until the credentials it holds expire, and is then
              stopped; one waiting for an approval or a consent is stopped when that is answered or expires; and
              none of them can be continued in chat. A posture scan that is already running, or was started just
              before you disconnect, is not stopped: it can read the account for {SCAN_READS_FOR}. Changes approved
              on this account that Torque Assist has not started applying are cancelled, and stay in their history:
              once the account is connected again, each must be proposed and approved again. While it is
              disconnected, no change on it can be approved. A disconnect does not undo a change Torque Assist has
              already started writing in AWS. Nothing is removed until you delete the stack in your AWS account,
              which removes{" "}
              {account.operator_role_arn ? "the read-only role and the operator role" : "the read-only role"}.
            </span>
          </Notice>
          {refusal === null ? null : (
            <Notice tone="bad" focusRef={refused}>
              {refusal}
            </Notice>
          )}
          <div className="actions">
            <button type="button" className="btn btn--small btn--danger" disabled={disconnecting} onClick={() => void disconnect()}>
              {disconnecting ? "Disconnecting…" : "Yes, disconnect"}
            </button>
            <button
              type="button"
              className="btn btn--small"
              disabled={disconnecting}
              onClick={() => {
                setConfirming(false);
                setRefusal(null);
                setFocusOn("opener");
              }}
            >
              Keep it connected
            </button>
          </div>
        </div>
      ) : onRename === undefined && !mayDisconnect ? null : editing ? (
        <form className="row row--wrap" onSubmit={save} aria-busy={busy}>
          <Field
            id={`account-name-${account.id}`}
            label="Account name"
            hint={`${ACCOUNT_NAME_RULE} Leave it empty to show the account id instead.`}
            maxLength={ACCOUNT_NAME_MAX_CHARS}
            value={name}
            error={error}
            onChange={(event) => setName(event.target.value)}
          />
          <button type="submit" className="btn btn--primary btn--small" disabled={busy}>
            {busy ? "Saving…" : "Save name"}
          </button>
          <button
            type="button"
            className="btn btn--small"
            disabled={busy}
            onClick={() => {
              setEditing(false);
              setError(null);
              setName(account.name ?? "");
            }}
          >
            Cancel
          </button>
        </form>
      ) : (
        <div className="actions">
          {onRename === undefined ? null : (
            <button
              type="button"
              className="btn btn--small"
              onClick={() => {
                setName(account.name ?? "");
                setEditing(true);
              }}
            >
              {account.name === null ? "Name this account" : "Rename"}
            </button>
          )}
          {mayDisconnect ? (
            <button
              type="button"
              className="btn btn--small btn--danger"
              ref={opener}
              onClick={() => {
                setConfirming(true);
                setFocusOn("question");
              }}
            >
              Disconnect
            </button>
          ) : null}
        </div>
      )}
      <details><summary>Connection details</summary>
        <dl className="setup-details">
        {account.name === null ? null : <><dt>AWS account id</dt><dd><code>{account.account_id}</code></dd></>}
        <dt>Read-only role</dt><dd><code>{account.role_arn}</code></dd>
        <dt>Connected</dt><dd>{account.connected_at.slice(0, 10)}</dd></dl>
      </details>
    </li>
  );
}

/**
 * How long credentials issued for an account last, in minutes: the platform's vend
 * (`control_plane/run_services.py::CREDENTIAL_DURATION_SECONDS`), which a test on that side
 * holds this copy to. An investigation running at a disconnect reads no longer than this.
 */
const CREDENTIAL_MINUTES = 15;

/**
 * How long a posture scan can go on reading an account: the session the scanner assumes for
 * itself (`scanner/prowler_runner.py::SESSION_SECONDS`, held to this by the same test). A
 * disconnect does not stop a scan that is running or was just started, and both notices say so.
 */
const SCAN_READS_FOR = "up to an hour";

/**
 * What a disconnect has done and what it leaves to do (#828, #1093): nothing new starts and
 * investigations get no more credentials, a scan in flight is not stopped, and each role the
 * stack made is still there until the stack is deleted. Focus lands here once the disconnect
 * has happened, since the confirmation that held it has gone.
 */
function Disconnected({
  stacks,
  operatorRole,
  focusRef,
}: {
  stacks: Connection[];
  /** Whether the account's stack made an operator role too (#262): then both stay. */
  operatorRole: boolean;
  focusRef: Ref<HTMLParagraphElement>;
}) {
  return (
    <div className="stack stack--tight" data-testid="disconnected">
      <Notice focusRef={focusRef}>
        <span>
          Torque Assist starts no new investigations or posture scans on this account, no longer accepts alarms
          from its topics, and gives its investigations no more credentials for it. An investigation that was
          running when the account was disconnected could read it for up to {CREDENTIAL_MINUTES} minutes more, and
          none of its investigations can be continued in chat. A posture scan that was running, or had just been
          started, was not stopped: it could read the account for {SCAN_READS_FOR}. Changes that were approved on
          this account and not yet being applied were cancelled, and stay in their history: once the account is
          connected again, each must be proposed and approved again. A change Torque Assist had already started
          writing in AWS was not undone.{" "}
          {operatorRole
            ? "The read-only role and the operator role stay in your AWS account until their stack is deleted. To remove them,"
            : "The read-only role stays in your AWS account until its stack is deleted. To remove the role,"}{" "}
          {stacks.length === 0 ? (
            "delete the stack that connected this account in CloudFormation."
          ) : (
            <>
              delete the stack{" "}
              {stacks.map((stack, index) => (
                <Fragment key={stack.id}>
                  {index === 0 ? null : index === stacks.length - 1 ? " and " : ", "}
                  <code>{stack.stack_name}</code>
                </Fragment>
              ))}{" "}
              in CloudFormation.
            </>
          )}{" "}
          To connect the account again, use Connect account and launch the stack it gives you.
        </span>
      </Notice>
      {stacks.length === 0 ? null : (
        <div className="actions">
          {stacks.map((stack) => (
            <a key={stack.id} className="btn btn--small" href={stack.stack_events_url} target="_blank" rel="noreferrer noopener">
              Open {stack.stack_name} in CloudFormation
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/** Configure → Deploy → Verify → Done, on the step flow the Jira and Slack flows use. */
function ConnectFlow({
  flow,
  live,
  launchAvailable,
  focusOnOpen,
  onStart,
  onLaunched,
  onRelaunch,
  onCheck,
  onCancel,
  onAnother,
  onClose,
  onSetDryRun,
}: {
  flow: ConnectFlowAt;
  /** The started connection as the latest read has it; null before anything is started. */
  live: Started | null;
  launchAvailable: boolean;
  /** Opened by a button that has now gone, so the step's heading takes focus from the start. */
  focusOnOpen: boolean;
  onStart: (regions: string[], name: string | null) => Promise<void>;
  onLaunched: () => void;
  onRelaunch: (connectionId: string) => Promise<void>;
  onCheck: (connectionId: string, accountId: string | null) => Promise<Connection>;
  onCancel?: (connectionId: string) => Promise<void>;
  onAnother: () => void;
  /** Absent when there is no account yet: the flow is then the page's only way on. */
  onClose?: () => void;
  onSetDryRun?: (sourceId: string, days: number) => Promise<unknown>;
}) {
  const step = connectStepOf(flow, live);
  const heading = useRef<HTMLHeadingElement>(null);
  const rendered = useRef(false);
  // Each step replaces the last one's controls, so its heading takes focus, as in the
  // Jira flow; on the first render only when the button the person pressed has gone.
  useEffect(() => {
    if (rendered.current || focusOnOpen) heading.current?.focus();
    rendered.current = true;
  }, [step, focusOnOpen]);
  const close =
    onClose === undefined ? null : (
      <button type="button" className="btn" onClick={onClose}>
        {step === "configure" ? "Cancel" : "Close"}
      </button>
    );
  let body: ReactNode;
  if (step === "configure" || live === null) {
    body = <Configure launchAvailable={launchAvailable} onStart={onStart} close={close} />;
  } else if (step === "deploy") {
    body = <Deploy started={live} onLaunched={onLaunched} close={close} />;
  } else if (step === "verify") {
    body = (
      <>
        <VerifyStages connection={live} />
        <SlowStack connection={live} launchAvailable={launchAvailable} onCancel={onCancel} />
        <CheckControls connection={live} onCheck={onCheck} onRelaunch={onRelaunch} extra={close} />
      </>
    );
  } else {
    body = (
      <>
        <Notice tone="good">
          {/* One run of text: the notice lays out its children side by side. */}
          <span>
            Role verified. <AccountName id={live.account_id ?? ""} name={live.name} /> is connected, and investigations
            read it through the read-only role.
          </span>
        </Notice>
        <DoneActions
          madeSourceId={onSetDryRun === undefined ? null : (live.created_alert_source_id ?? null)}
          onSetDryRun={onSetDryRun}
          onAnswered={() => heading.current?.focus()}
          onAnother={onAnother}
          close={close}
        />
      </>
    );
  }
  return (
    <StepFlow title="Connect an AWS account" testId="connect-flow" steps={CONNECT_STEPS} current={step}>
      <h4 ref={heading} tabIndex={-1} className="step-flow__title">
        {CONNECT_STEPS.find((each) => each.id === step)?.label}
      </h4>
      {body}
    </StepFlow>
  );
}

/**
 * Done's way on. Where the connection made the workspace's alert source (#860), a week's
 * dry run on it is offered first, ticked, as the Alert sources screen offers one at setup
 * (#849): Continue starts it, or, unticked, sends nothing and leaves the source live.
 */
function DoneActions({
  madeSourceId,
  onSetDryRun,
  onAnswered,
  onAnother,
  close,
}: {
  madeSourceId: string | null;
  onSetDryRun?: (sourceId: string, days: number) => Promise<unknown>;
  /** The form that held the focus is replaced by the answer: the step's heading takes it. */
  onAnswered: () => void;
  onAnother: () => void;
  close: ReactNode;
}) {
  const [dryRun, setDryRun] = useState(true);
  const [answer, setAnswer] = useState<"dry-run" | "live" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (answer !== null) onAnswered();
    // Once, when the answer replaces the form.
  }, [answer]);
  if (madeSourceId !== null && onSetDryRun !== undefined && answer === null) {
    const submit = async (event: FormEvent) => {
      event.preventDefault();
      setError(null);
      if (!dryRun) {
        setAnswer("live");
        return;
      }
      setBusy(true);
      try {
        await onSetDryRun(madeSourceId, DEFAULT_DRY_RUN_DAYS);
        setAnswer("dry-run");
      } catch (failure) {
        setError(messageOf(failure));
      } finally {
        setBusy(false);
      }
    };
    return (
      <form className="stack" onSubmit={submit} aria-busy={busy} data-testid="connect-dry-run">
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        <label className="checkline">
          <input type="checkbox" checked={dryRun} onChange={(event) => setDryRun(event.target.checked)} />
          <span>
            Start the alert source with a {DEFAULT_DRY_RUN_DAYS}-day dry run{" "}
            <small className="field__hint">
              Connecting this account made the workspace's alert source. During a dry run, alarms that would start an
              investigation are listed on the source and start nothing, so you can tune which alarms start one before
              switching it on.
            </small>
          </span>
        </label>
        <div className="actions">
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Starting…" : "Continue"}
          </button>
          {close}
        </div>
      </form>
    );
  }
  return (
    <>
      {answer === null ? null : (
        <p className="muted" role="status">
          {answer === "dry-run"
            ? `The alert source is in a dry run for ${DEFAULT_DRY_RUN_DAYS} days.`
            : "The alert source is live: its alarms start investigations."}
        </p>
      )}
      <div className="actions">
        <a className="btn btn--primary" href="/sources">
          Go to alert sources
        </a>
        <button type="button" className="btn" onClick={onAnother}>
          Add another account
        </button>
        {close}
      </div>
    </>
  );
}

/** Configure: what the stack creates, then the name and the regions. */
function Configure({
  launchAvailable,
  onStart,
  close,
}: {
  launchAvailable: boolean;
  onStart: (regions: string[], name: string | null) => Promise<void>;
  close: ReactNode;
}) {
  const [regions, setRegions] = useState<string[]>(["eu-west-1"]);
  const [name, setName] = useState("");
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toggle = (region: string) =>
    setRegions((was) => (was.includes(region) ? was.filter((r) => r !== region) : [...was, region]));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // On success the flow moves to Deploy and this form is gone.
      await onStart(regions, name.trim() === "" ? null : name.trim());
    } catch (failure) {
      setError(messageOf(failure));
      setBusy(false);
    }
  };
  return (
    <form className="setup-account-grid" onSubmit={submit} aria-busy={busy}>
      <div className="stack stack--tight">
        <h5>What the stack creates in your account</h5>
        <ul className="facts">
          <li>
            <strong>One IAM role with AWS's ReadOnlyAccess policy.</strong> Investigations read
            configuration, metrics, logs metadata and CloudTrail through it.
          </li>
          <li>
            <strong>A trust policy naming this platform's account and an external id</strong> that
            Torque Assist mints and keeps. Nobody else can assume the role, and you never have to
            handle the id.
          </li>
          <li>
            <strong>A one-time callback</strong> that tells Torque Assist the role exists, signed with
            a secret only this connection holds.
          </li>
        </ul>
        <h5>What it never does</h5>
        <ul className="facts">
          <li>It cannot change, create or delete anything in your account.</li>
          <li>
            It does not read the <em>contents</em> of your resources — a log line, an object, a
            parameter's value. Each of those is a consent you answer per request, in the Inbox
            under {INBOX_KINDS.consent_request.label}.
          </li>
          <li>Operator access for the platform's own staff is not part of this template.</li>
        </ul>
        <p className="muted">
          You can revoke everything at any time by deleting the stack in your account.
        </p>
      </div>
      <div className="stack setup-account-options">
        <Field
          id="connect-account-name"
          label="Account name (optional)"
          hint={`What your team calls this account, such as Production, shown instead of the 12-digit id. ${ACCOUNT_NAME_RULE}`}
          maxLength={ACCOUNT_NAME_MAX_CHARS}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <fieldset>
          <legend className="field__label">Regions to investigate</legend>
          <div className="setup-region-grid">
            {REGIONS.map(([code, label]) => (
              <label key={code} className="checkline">
                <input
                  type="checkbox"
                  name="regions"
                  value={code}
                  checked={regions.includes(code)}
                  onChange={() => toggle(code)}
                />
                <span>
                  {label} <small className="muted">{code}</small>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <label className="checkline">
          <input
            type="checkbox"
            checked={understood}
            onChange={(event) => setUnderstood(event.target.checked)}
          />
          <span>I understand what the stack creates and that it is read-only.</span>
        </label>
        {launchAvailable ? null : (
          <Notice>
            This estate has not published its template yet, so there is no one-click link: the
            next step gives you the template and its parameters to launch by hand.
          </Notice>
        )}
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        <div className="actions">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy || !understood || regions.length === 0}
          >
            {busy ? "Preparing…" : "Continue to deploy"}
          </button>
          {close}
        </div>
      </div>
    </form>
  );
}

const COPY_REFUSED = "This browser did not allow the copy. Select the value and copy it with Ctrl+C or ⌘C.";

/** Deploy: the launch, by link or by hand, each piece with its own copy button. */
function Deploy({ started, onLaunched, close }: { started: Started; onLaunched: () => void; close: ReactNode }) {
  const { launch } = started;
  const [said, setSaid] = useState<string | null>(null);
  const copy = async (what: string, text: string) => {
    const copied = await copyText(text, typeof navigator === "undefined" ? undefined : navigator.clipboard);
    setSaid(copied ? `Copied ${what}.` : COPY_REFUSED);
  };
  return (
    <>
      {launch.launch_url !== null ? (
        <>
          <p>
            The link opens CloudFormation's quick-create page in your own console with every
            parameter filled in. Review it, tick the IAM acknowledgement, and create the stack. If
            someone else manages your AWS account, copy the link and send it to them.
          </p>
          <div className="actions">
            <a className="btn btn--primary" href={launch.launch_url} target="_blank" rel="noreferrer noopener">
              Launch in AWS
            </a>
            <button type="button" className="btn" onClick={() => void copy("the launch link", launch.launch_url ?? "")}>
              Copy link
            </button>
            <span className="muted">
              Stack name <code>{launch.stack_name}</code>
            </span>
          </div>
        </>
      ) : (
        <>
          <p>
            Download the template and create a stack from it in your CloudFormation console, with
            these parameters.
          </p>
          <div className="actions">
            <a className="btn btn--primary" href={TEMPLATE_PATH}>
              Download the template
            </a>
            <span className="muted">
              Stack name <code>{launch.stack_name}</code>
            </span>
          </div>
          <dl className="launch-params" aria-label="Stack parameters">
            {Object.entries(launch.parameters).map(([name, value]) => (
              <div key={name}>
                <dt>{name}</dt>
                <dd>
                  <code className="break">{value === "" ? "(leave empty)" : value}</code>
                  {value === "" ? null : (
                    <button
                      type="button"
                      className="btn btn--small"
                      aria-label={`Copy ${name}`}
                      onClick={() => void copy(name, value)}
                    >
                      Copy
                    </button>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </>
      )}
      <p>
        Until the account connects, and for up to {PENDING_LIFETIME_DAYS} days after this
        connection started, “Show the launch again” brings back this same launch, external id and
        secret included.
      </p>
      <small className="field__hint" role="status" data-testid="connect-copied">
        {said}
      </small>
      <p className="muted">Regions: {started.regions.join(", ")}.</p>
      <div className="actions">
        <button type="button" className="btn btn--primary" onClick={onLaunched}>
          I have launched it
        </button>
        {close}
      </div>
    </>
  );
}

/** Verify: where the connection is on its way to a verified role, as the latest read says. */
function VerifyStages({ connection }: { connection: Connection }) {
  const reported = connection.state !== "awaiting_stack" || connection.role_arn !== null;
  const verified =
    connection.state === "connected"
      ? "Done"
      : connection.state === "failed"
        ? "Not yet"
        : connection.state === "verifying"
          ? "Checking now"
          : "Next";
  const stages: Array<[string, string]> = [
    ["Stack reported in", reported ? "Done" : "Waiting for your stack"],
    ["Role verified", verified],
  ];
  return (
    <>
      <div className="row row--between row--wrap">
        <span>
          Stack <code>{connection.stack_name}</code>
        </span>
        <Chip tone={STATE_TEXT[connection.state].tone}>{STATE_TEXT[connection.state].label}</Chip>
      </div>
      <ol className="step-flow__needs" aria-label="Verification" data-testid="connect-stages">
        {stages.map(([label, where]) => (
          <li key={label}>
            <strong>{label}</strong> <span className="muted">· {where}</span>
          </li>
        ))}
      </ol>
      {connection.error === null ? null : <Notice tone="bad">{connection.error}</Notice>}
    </>
  );
}

/**
 * "Check again", with the account id asked for when the stack's callback never arrived,
 * and the way back to the launch: the flow's Verify step and each connection in progress.
 */
function CheckControls({
  connection,
  onCheck,
  onRelaunch,
  extra = null,
}: {
  connection: Connection;
  onCheck: (connectionId: string, accountId: string | null) => Promise<Connection>;
  onRelaunch: (connectionId: string) => Promise<void>;
  extra?: ReactNode;
}) {
  const [accountId, setAccountId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needsAccount = connection.role_arn === null;
  const act = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {connection.state === "awaiting_stack" && needsAccount ? (
        <p className="muted">
          Waiting for your stack to report in; this page checks by itself every few seconds. If
          it has finished and nothing arrived here, tell us which account you launched it in and
          we will look for the role there.
        </p>
      ) : null}
      {connection.state === "verifying" ? <p className="muted" role="status">The stack reported in. Torque Assist is checking that it can assume the read-only role. Check again for the result.</p> : null}
      {connection.state === "failed" ? <p className="muted">Review the stack in AWS, then check again. A connection still not connected {PENDING_LIFETIME_DAYS} days after it started expires; then start a new connection and launch the stack it gives you.</p> : null}
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      <div className="row row--wrap">
        {needsAccount ? (
          <Field
            id={`account-${connection.id}`}
            label="AWS account id"
            inputMode="numeric"
            pattern="[0-9]{12}"
            placeholder="123456789012"
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
          />
        ) : null}
        <button
          type="button"
          className="btn btn--primary btn--small"
          disabled={busy}
          onClick={() => void act(() => onCheck(connection.id, needsAccount ? accountId.trim() || null : null))}
        >
          {busy ? "Checking…" : "Check again"}
        </button>
        <button
          type="button"
          className="btn btn--small"
          disabled={busy}
          onClick={() => void act(() => onRelaunch(connection.id))}
        >
          Show the launch again
        </button>
        {extra}
      </div>
    </>
  );
}

/** A connection that is not yet an account, outside the flow: its state and the ways on. */
function Pending({
  connection,
  launchAvailable,
  onCheck,
  onRelaunch,
  onCancel,
}: {
  connection: Connection;
  /** Whether the stack was launched by the link, and so in the region the link names. */
  launchAvailable: boolean;
  /** Absent for someone the server would refuse: the checks are not theirs to run. */
  onCheck?: (connectionId: string, accountId: string | null) => Promise<Connection>;
  onRelaunch: (connectionId: string) => Promise<void>;
  /** Absent for someone the server would refuse, as `onCheck` is. */
  onCancel?: (connectionId: string) => Promise<void>;
}) {
  const text = STATE_TEXT[connection.state];
  return (
    <div className="panel__body stack stack--tight" data-testid="connection">
      <div className="row row--between row--wrap">
        <div>
          <strong>
            Stack <code>{connection.stack_name}</code>
          </strong>
          <br />
          <small className="muted">
            Started {connection.created_at.slice(0, 16).replace("T", " ")} · regions{" "}
            {connection.regions.join(", ")}
            {connection.account_id ? ` · account ${connection.account_id}` : ""}
          </small>
        </div>
        <Chip tone={text.tone}>{text.label}</Chip>
      </div>
      {connection.error === null ? null : <Notice tone="bad">{connection.error}</Notice>}
      <SlowStack connection={connection} launchAvailable={launchAvailable} onCancel={onCancel} />
      {onCheck === undefined ? null : <CheckControls connection={connection} onCheck={onCheck} onRelaunch={onRelaunch} />}
    </div>
  );
}

/**
 * A stack that has not reported in ten minutes after its connection started (#825): its
 * events in CloudFormation, which say why a stack failed or rolled back; keep waiting, which
 * asks again ten minutes on; or cancel, after which the server refuses the stack's callback.
 */
function SlowStack({
  connection,
  launchAvailable,
  onCancel,
}: {
  connection: Connection;
  /**
   * Whether the stack was launched by the link, in the region the link names. By hand the
   * customer chose the region, so the link names the one it opens rather than claiming it.
   */
  launchAvailable: boolean;
  /** Absent for someone the server would refuse. */
  onCancel?: (connectionId: string) => Promise<void>;
}) {
  const [waitedAt, setWaitedAt] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const due = slowDueAt(connection, waitedAt);
  const now = useNowAt(due);
  if (due === null || now < due) return null;
  const waited = elapsedWords(now - Date.parse(connection.created_at));
  const region = new URL(connection.stack_events_url).searchParams.get("region") ?? "";
  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      await onCancel?.(connection.id);
    } catch (failure) {
      setError(messageOf(failure));
      setBusy(false);
    }
  };
  return (
    <div className="stack stack--tight" data-testid="slow-stack">
      <Notice>
        <span>
          Stack <code>{connection.stack_name}</code> has not reported in, {waited} after this
          connection started. A stack usually finishes in two or three minutes; if it failed or rolled
          back, its events in CloudFormation say why.
        </span>
      </Notice>
      {confirming ? (
        <Notice>
          <span>
            Cancel this connection? Torque Assist stops waiting for the stack, and refuses it if it
            reports in later. If the stack is in your account, delete it in CloudFormation.
          </span>
        </Notice>
      ) : null}
      {launchAvailable ? null : (
        <p className="muted">If you created the stack in another region, open its events there.</p>
      )}
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      <div className="actions">
        <a className="btn" href={connection.stack_events_url} target="_blank" rel="noreferrer noopener">
          {launchAvailable
            ? "Open the stack's events in CloudFormation"
            : `Open the stack's events in CloudFormation in ${region}`}
        </a>
        {confirming ? (
          <>
            <button type="button" className="btn btn--danger" disabled={busy} onClick={() => void cancel()}>
              {busy ? "Cancelling…" : "Yes, cancel it"}
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => setConfirming(false)}>
              Back
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setWaitedAt(Date.now());
                setError(null);
              }}
            >
              Keep waiting
            </button>
            {onCancel === undefined ? null : (
              <button type="button" className="btn btn--danger" onClick={() => setConfirming(true)}>
                Cancel connection
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
